import type { MockupType } from './types';
import type { UploadRatio } from './components/ad-details/shared';

export type { UploadRatio };

// ─── Ratio normaliser ─────────────────────────────────────────────────────────
// The DB stores whatever string came from campaign.size (e.g. '1440x1440',
// '1440x2560', '1x1', '9x16'). Normalise to canonical '1x1' or '9x16' so we
// can find the right asset regardless of how the old modal stored the ratio.

export function normalizeRatio(ratio: string | null | undefined): '1x1' | '9x16' | null {
  if (!ratio) return null;
  const r = ratio.toLowerCase().replace(/\s/g, '');
  if (r === '1x1' || r === '1:1' || r === '4x5' || r === '4:5') return '1x1';
  if (r === '9x16' || r === '9:16') return '9x16';
  // Parse pixel dimensions e.g. '1440x1440' or '1440x2560'
  const parts = r.split('x');
  if (parts.length === 2) {
    const w = parseInt(parts[0], 10);
    const h = parseInt(parts[1], 10);
    if (!isNaN(w) && !isNaN(h) && h > 0) {
      const ar = w / h;
      if (Math.abs(ar - 1) < 0.05) return '1x1'; // square ≈ 1:1
      if (Math.abs(ar - 4 / 5) < 0.05) return '1x1'; // portrait ≈ 4:5 → feed slot
      if (Math.abs(ar - 9 / 16) < 0.05) return '9x16'; // vertical ≈ 9:16
    }
  }
  return null;
}

// ─── Exact ratio reader ───────────────────────────────────────────────────────
// Unlike normalizeRatio (above), this distinguishes 4:5 from 1:1. Use it ONLY to
// resolve which *upload slot* an asset belongs to, never for mockup/tab grouping
// — the mockup deliberately treats 1:1 and 4:5 as one shared feed format.
//
// IMPORTANT — legacy data: assets uploaded through the pre-4x5 workaround were
// persisted with the ratio of the SLOT they were dropped into, not their measured
// dimensions (see campaign_asset_service.py `ratio=image_ratio`). A historical 4:5
// file is therefore stored as '1x1' and is indistinguishable from a true 1:1 here.
// That is intentional and load-bearing: those assets stay in the 1:1 slot where the
// user put them, so existing campaigns render exactly as they always have. Only
// uploads made through the new 4:5 slot persist ratio='4x5'.

export function exactRatio(ratio: string | null | undefined): UploadRatio | null {
  if (!ratio) return null;
  const r = ratio.toLowerCase().replace(/\s/g, '');
  if (r === '1x1' || r === '1:1') return '1x1';
  if (r === '4x5' || r === '4:5') return '4x5';
  if (r === '9x16' || r === '9:16') return '9x16';
  // Parse pixel dimensions e.g. '1440x1440', '1080x1350'
  const parts = r.split('x');
  if (parts.length === 2) {
    const w = parseInt(parts[0], 10);
    const h = parseInt(parts[1], 10);
    if (!isNaN(w) && !isNaN(h) && h > 0) {
      const ar = w / h;
      if (Math.abs(ar - 1) < 0.05) return '1x1';
      if (Math.abs(ar - 4 / 5) < 0.05) return '4x5';
      if (Math.abs(ar - 9 / 16) < 0.05) return '9x16';
    }
  }
  return null;
}

// ─── Ratio config per format ──────────────────────────────────────────────────
// Only formats that support multiple upload ratios show ratio tabs.
// When a ratio is selected, it also drives which mockup variant is shown.

export type Ratio = '1x1' | '9x16' | 'reel';

export const FORMAT_RATIOS: Partial<Record<MockupType, Ratio[]>> = {
  meta: ['1x1', '9x16', 'reel'],
  'meta-carousel': ['1x1', '9x16'],
  'meta-collection': ['1x1', '9x16'],
  'meta-dynamic-creative': ['1x1', '9x16', 'reel'],
};

export const RATIO_LABELS: Record<Ratio, string> = {
  '1x1': '1:1 / 4:5 Feed',
  '9x16': '9:16 Story',
  reel: '9:16 Reel',
};

// When 9:16 or reel is selected for feed formats, swap to the matching placement mockup
export function resolveEffectiveMockupType(type: MockupType, ratio: Ratio): MockupType {
  if (ratio === 'reel') {
    if (type === 'meta' || type === 'meta-dynamic-creative') return 'meta-reels';
  }
  if (ratio === '9x16') {
    if (type === 'meta' || type === 'meta-dynamic-creative') return 'meta-story';
    if (type === 'meta-carousel') return 'meta-story-carousel';
    if (type === 'meta-collection') return 'meta-story-collection';
  }
  // Dynamic Creative at 1:1, and Flexible Ad, both render as the standard feed mockup
  if (type === 'meta-dynamic-creative') return 'meta';
  if (type === 'meta-flexible-ad') return 'meta';
  return type;
}

// ─── CTA normaliser ──────────────────────────────────────────────────────────
/** Normalize the cta field from the API — may be string[] (post-migration), a JSON-encoded
 *  array string (saved after frontend change but before DB migration), or a plain string (pre-migration).
 *  Also flattens any elements that are themselves JSON-encoded arrays (corrupted test data).
 *  Always returns a clean string[] with only non-empty string values. */
export function normalizeCta(cta: string | string[] | null | undefined): string[] {
  const sanitize = (values: unknown[]): string[] =>
    values.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);

  if (Array.isArray(cta)) {
    const flattened: unknown[] = cta.flatMap((item) => {
      if (typeof item === 'string' && item.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(item);
          if (Array.isArray(parsed)) return parsed;
        } catch {
          /* fall through */
        }
      }
      return [item];
    });
    return sanitize(flattened);
  }
  if (typeof cta === 'string' && cta) {
    if (cta.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(cta);
        if (Array.isArray(parsed)) return sanitize(parsed);
      } catch {
        /* fall through */
      }
    }
    return sanitize([cta]);
  }
  return [];
}
